import { createContext, useContext, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { apiGet, apiPost, apiPatch, apiDelete } from "src/configs/globalFunctions";

const MinipayParticipantesContext = createContext({
  participantes: [],
  loading: true,
  refresh: () => Promise.resolve([]),
  addParticipantes: () => Promise.resolve([]),
  registrarMovimentacao: () => Promise.resolve(null),
  atualizarParticipante: () => Promise.resolve(null),
  removerParticipante: () => Promise.resolve(),
});

export function MinipayParticipantesProvider({ children }) {
  const [participantes, setParticipantes] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => new Promise((resolve) => {
    apiGet("/minipay/participantes", {
      done: (resp) => {
        const lista = resp.data || [];
        setParticipantes(lista);
        resolve(lista);
      },
      error: () => resolve([]),
    });
  }), []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const addParticipantes = useCallback((novos) => new Promise((resolve, reject) => {
    apiPost("/minipay/participantes", {
      body: { participantes: novos },
      done: (resp) => {
        const criados = resp.data || [];
        setParticipantes((atual) => [...atual, ...criados]);
        resolve(criados);
      },
      error: () => reject(new Error("Falha ao cadastrar participantes")),
    });
  }), []);

  const registrarMovimentacao = useCallback((participanteId, { descricao, valor }) => (
    new Promise((resolve, reject) => {
      apiPost(`/minipay/participantes/${participanteId}/movimentacoes`, {
        body: { descricao, valor },
        done: (resp) => {
          const atualizado = resp.data;
          setParticipantes((atual) => atual.map((p) => (p.id === atualizado.id ? atualizado : p)));
          resolve(atualizado);
        },
        error: () => reject(new Error("Falha ao registrar movimentação")),
      });
    })
  ), []);

  const atualizarParticipante = useCallback((participanteId, { nome }) => (
    new Promise((resolve, reject) => {
      apiPatch(`/minipay/participantes/${participanteId}`, {
        body: { nome },
        done: (resp) => {
          const atualizado = resp.data;
          setParticipantes((atual) => atual.map((p) => (p.id === atualizado.id ? atualizado : p)));
          resolve(atualizado);
        },
        error: () => reject(new Error("Falha ao renomear participante")),
      });
    })
  ), []);

  const removerParticipante = useCallback((participanteId) => new Promise((resolve, reject) => {
    apiDelete(`/minipay/participantes/${participanteId}`, {
      done: () => {
        setParticipantes((atual) => atual.filter((p) => p.id !== participanteId));
        resolve();
      },
      error: () => reject(new Error("Falha ao excluir participante")),
    });
  }), []);

  return (
    <MinipayParticipantesContext.Provider
      value={{
        participantes, loading, refresh, addParticipantes, registrarMovimentacao, atualizarParticipante, removerParticipante,
      }}
    >
      {children}
    </MinipayParticipantesContext.Provider>
  );
}

MinipayParticipantesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useParticipantes() {
  return useContext(MinipayParticipantesContext);
}

export default MinipayParticipantesContext;
